import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MenuPermissionsProvider, useMenuPermissions } from './contexts/MenuPermissionsContext';
import { getPlatformLandingPath, MENU_REGISTRY, PlatformId } from './config/menuRegistry';
import { TenantProvider } from './contexts/TenantContext';
import { ChatNotificationsProvider } from './contexts/ChatNotificationsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { GlobalTopBar } from './components/GlobalTopBar';
import { MultiAccountNotifier } from './components/MultiAccountNotifier';
import { ContactsPage } from './pages/ContactsPage';
import { ContactsKanbanPage } from './pages/ContactsKanbanPage';
import { ContactLabelsPage } from './pages/ContactLabelsPage';
import { FunnelsPage } from './pages/FunnelsPage';
import { FormsPage } from './pages/FormsPage';
import { PublicFormPage } from './pages/PublicFormPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CategoryEditorPage } from './pages/CategoryEditorPage';
import { WhatsAppConnectionsPage } from './pages/WhatsAppConnectionsPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { GroupCampaignsPage } from './pages/GroupCampaignsPage';
import { GoogleMapsExtractorPage } from './pages/GoogleMapsExtractorPage';
import { CNPJSearchPage } from './pages/CNPJSearchPage';
import { AISettingsPage } from './pages/settings/AISettingsPage';
import { ChatSettingsPage } from './pages/settings/ChatSettingsPage';
import { LogsPage } from './pages/settings/LogsPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { FollowUpPage } from './pages/FollowUpPage';
import { MarketplacesPage } from './pages/MarketplacesPage';
import { MercadoLivrePage } from './pages/MercadoLivrePage';
import { ShopeePage } from './pages/ShopeePage';
import { ErpsPage } from './pages/ErpsPage';
import { TinyAutomationPage } from './pages/TinyAutomationPage';
import { WebhookAutomationPage } from './pages/WebhookAutomationPage';
import { SocialConnectionsPage } from './pages/SocialConnectionsPage';
import { CommentAutomationPage } from './pages/CommentAutomationPage';
import { PostingPage } from './pages/PostingPage';
import { WhatsAppTemplatesPage } from './pages/WhatsAppTemplatesPage';
import { UsersPage } from './pages/UsersPage';
import { LoginPage } from './pages/LoginPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { SuperAdminDashboard } from './pages/SuperAdminDashboard';
import { SuperAdminManagerPage } from './pages/SuperAdminManagerPage';
import { GroupContactExtractorPage } from './pages/GroupContactExtractorPage';
import { ERPContactSearchPage } from './pages/ERPContactSearchPage';
import { TestChatPage } from './pages/TestChatPage';
import { MarketingStudioPage } from './pages/MarketingStudioPage';
import { BusinessCorePage } from './pages/BusinessCorePage';
import { SellerPage } from './pages/SellerPage';
import { McpOAuthConsentPage } from './pages/McpOAuthConsentPage';

// Pedido de conexão OAuth do MCP (ex.: ChatGPT) sem login: guarda o endereço para voltar depois.
function RememberOAuthAndLogin() {
  try { sessionStorage.setItem('post_login_redirect', window.location.pathname + window.location.search); } catch { /* sem storage */ }
  return <Navigate to="/login" replace />;
}
import { FlowPage } from './pages/flow/FlowPage';
import { SocialPostsListPage } from './pages/SocialPostsListPage';
import { CrmChatPage } from './pages/CrmChatPage';
import { QuickRepliesPage } from './pages/QuickRepliesPage';
import { useGlobalSettings } from './hooks/useGlobalSettings';
import './styles/globals.css';

function PrimaryPlatformRedirect() {
  const { allowedMenus, loading, primaryPlatform, canSeePlatform } = useMenuPermissions();

  if (loading || !primaryPlatform) {
    return <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-500">Carregando...</div>;
  }

  const destinationPlatform = canSeePlatform(primaryPlatform)
    ? primaryPlatform
    : (MENU_REGISTRY.find(platform => canSeePlatform(platform.id))?.id || 'crm') as PlatformId;

  return <Navigate to={getPlatformLandingPath(destinationPlatform, allowedMenus)} replace />;
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { settings } = useGlobalSettings();
  const location = useLocation();
  const isMarketingPlatform = location.pathname.startsWith('/marketing-studio') || location.pathname.startsWith('/business-core') || location.pathname.startsWith('/seller') || location.pathname.startsWith('/flow');
  const isAddingAccount = location.pathname === '/login' && new URLSearchParams(location.search).get('addAccount') === '1';
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fecha o drawer da sidebar ao navegar (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Aplicar meta tags dinâmicas (título e favicon)
  useEffect(() => {
    // Atualizar título da página
    if (settings?.pageTitle) {
      document.title = settings.pageTitle;
    }

    // Atualizar favicon
    if (settings?.faviconUrl) {
      // Remover favicons existentes
      const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
      existingFavicons.forEach(favicon => favicon.remove());

      // Adicionar novo favicon com cache busting
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/png';
      favicon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(favicon);

      // Adicionar também como shortcut icon para compatibilidade
      const shortcutIcon = document.createElement('link');
      shortcutIcon.rel = 'shortcut icon';
      shortcutIcon.type = 'image/png';
      shortcutIcon.href = settings.faviconUrl + '?v=' + Date.now();
      document.head.appendChild(shortcutIcon);
    }
  }, [settings?.pageTitle, settings?.faviconUrl]);

  // Remove any banners dynamically - more specific targeting
  useEffect(() => {
    const removeBanners = () => {
      // Remove elements with specific version banner text
      const elements = Array.from(document.querySelectorAll('*')).filter(el =>
        el.textContent && el.textContent.includes('BUILD 2025-09-17-19:02')
      );
      elements.forEach(el => {
        el.remove();
      });
    };

    // Run on mount
    removeBanners();

    // Run every 5 seconds (less aggressive)
    const interval = setInterval(removeBanners, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/formularios/publico/:slug" element={<PublicFormPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/mcp" element={<RememberOAuthAndLogin />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Tela de permissão do OAuth do MCP: sozinha, sem o menu do app.
  if (window.location.pathname === '/oauth/mcp') return <McpOAuthConsentPage />;

  return (
    <div className="min-h-screen bg-slate-50">
      <MultiAccountNotifier />
      <GlobalTopBar
        showMenuButton={!isMarketingPlatform}
        onMenuClick={() => setSidebarOpen((open) => !open)}
      />
      {!isMarketingPlatform && <Navigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      {/* Overlay do drawer (apenas mobile) */}
      {!isMarketingPlatform && sidebarOpen && (
        <div
          className="fixed inset-0 top-11 z-[1050] bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className={`${isMarketingPlatform ? '' : 'main-content'} flex-1 flex flex-col pt-11`}>
        <Routes>
          <Route path="/login" element={isAddingAccount ? <LoginPage /> : <Navigate to="/" replace />} />
          <Route path="/" element={<PrimaryPlatformRedirect />} />
          <Route
            path="/contatos"
            element={
              <ProtectedRoute>
                <ContactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contatos/kanban"
            element={
              <ProtectedRoute>
                <ContactsKanbanPage />
              </ProtectedRoute>
            }
          />
          <Route path="/contatos/etiquetas" element={<ProtectedRoute><ContactLabelsPage /></ProtectedRoute>} />
          <Route
            path="/funis"
            element={
              <ProtectedRoute>
                <FunnelsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/formularios"
            element={
              <ProtectedRoute>
                <FormsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/formularios/publico/:slug" element={<PublicFormPage />} />
          <Route
            path="/categorias"
            element={
              <ProtectedRoute>
                <CategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categorias/nova"
            element={
              <ProtectedRoute>
                <CategoryEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categorias/:id"
            element={
              <ProtectedRoute>
                <CategoryEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat-crm"
            element={
              <ProtectedRoute>
                <CrmChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/respostas-rapidas"
            element={
              <ProtectedRoute>
                <QuickRepliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automacoes-webhook"
            element={
              <ProtectedRoute>
                <WebhookAutomationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/whatsapp"
            element={
              <ProtectedRoute>
                <WhatsAppConnectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campanhas"
            element={<Navigate to="/campanhas-contatos" replace />}
          />
          <Route
            path="/campanhas-contatos"
            element={
              <ProtectedRoute>
                <CampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grupos"
            element={<Navigate to="/campanhas-grupos" replace />}
          />
          <Route
            path="/campanhas-grupos"
            element={
              <ProtectedRoute>
                <GroupCampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/erp-contatos"
            element={
              <ProtectedRoute>
                <ERPContactSearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/extrator-grupos"
            element={
              <ProtectedRoute>
                <GroupContactExtractorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/google-maps"
            element={
              <ProtectedRoute>
                <GoogleMapsExtractorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cnpj"
            element={
              <ProtectedRoute>
                <CNPJSearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/base-conhecimento"
            element={
              <ProtectedRoute>
                <KnowledgeBasePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-chat"
            element={
              <ProtectedRoute>
                <TestChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-core/*"
            element={
              <ProtectedRoute>
                <BusinessCorePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketing-studio/*"
            element={
              <ProtectedRoute>
                <MarketingStudioPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/*"
            element={
              <ProtectedRoute>
                <SellerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/flow/*"
            element={
              <ProtectedRoute>
                <FlowPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/follow-up"
            element={
              <ProtectedRoute>
                <FollowUpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplaces"
            element={
              <ProtectedRoute>
                <MarketplacesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mercado-livre"
            element={
              <ProtectedRoute>
                <MercadoLivrePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shopee"
            element={
              <ProtectedRoute>
                <ShopeePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/erps"
            element={
              <ProtectedRoute>
                <ErpsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tiny-erp/automacao"
            element={
              <ProtectedRoute>
                <TinyAutomationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/redes-sociais"
            element={
              <ProtectedRoute>
                <SocialConnectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automacao-comentarios"
            element={
              <ProtectedRoute>
                <CommentAutomationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/postar"
            element={
              <ProtectedRoute>
                <PostingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/postagens"
            element={
              <ProtectedRoute>
                <SocialPostsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/whatsapp-templates"
            element={
              <ProtectedRoute>
                <WhatsAppTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuracoes"
            element={<Navigate to="/configuracoes-ia" replace />}
          />
          <Route
            path="/configuracoes-ia"
            element={
              <ProtectedRoute>
                <AISettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuracoes-chat"
            element={
              <ProtectedRoute>
                <ChatSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/configuracoes-logs"
            element={
              <ProtectedRoute>
                <LogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute superAdminOnly={true}>
                <SuperAdminManagerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/dashboard"
            element={
              <ProtectedRoute superAdminOnly={true}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/tenants"
            element={
              <ProtectedRoute superAdminOnly={true}>
                <SuperAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/usuarios"
            element={
              <ProtectedRoute adminOnly={true}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

// Página de desenvolvimento do editor edvid sobre um fixture (sem login nem backend).
// Só existe em `vite dev`: no build de produção import.meta.env.DEV é false e o
// import dinâmico some do bundle.
const EdvidEditorDevPage = import.meta.env.DEV ? lazy(() => import('./pages/dev/EdvidEditorDevPage')) : null;

function App() {
  if (EdvidEditorDevPage && window.location.pathname === '/dev/edvid-editor') {
    return (
      <Suspense fallback={null}>
        <EdvidEditorDevPage />
        <Toaster position="top-right" containerStyle={{ top: 80, zIndex: 2147483000 }} />
      </Suspense>
    );
  }
  return (
    <Router>
      <AuthProvider>
        <TenantProvider>
          <MenuPermissionsProvider>
          <ChatNotificationsProvider>
          <AppContent />
          <Toaster
            position="top-right"
            containerStyle={{
              top: 80,
              // O container do react-hot-toast usa zIndex 9999 por padrão — o MESMO
              // valor de vários modais do app (CampaignsPage, WhatsAppConnectionsPage).
              // Em empate de z-index decide a ordem no DOM, e os modais são portalados
              // no body DEPOIS do Toaster, então cobriam os avisos. Toast é sempre a
              // camada mais alta: fica acima de qualquer modal.
              zIndex: 2147483000,
            }}
            toastOptions={{
              duration: 4000,
              className: '',
              style: {
                background: '#1e293b',
                color: '#fff',
                borderRadius: '12px',
                fontSize: '14px',
                padding: '12px 16px',
                paddingRight: '40px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              },
              success: {
                className: '',
                style: {
                  background: '#10b981',
                  color: '#fff',
                },
              },
              error: {
                className: '',
                style: {
                  background: '#ef4444',
                  color: '#fff',
                },
              },
              loading: {
                className: '',
                style: {
                  background: '#1e293b',
                  color: '#fff',
                },
              },
            }}
          >
            {(t) => {
              const backgroundColor =
                t.type === 'success' ? '#10b981' :
                t.type === 'error' ? '#ef4444' :
                '#1e293b';

              return (
                <div
                  className="flex items-center gap-2"
                  style={{
                    position: 'relative',
                    background: backgroundColor,
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    paddingRight: '40px',
                    fontSize: '14px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                >
                  {/* Ícone baseado no tipo */}
                  {t.type === 'success' && (
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                  {t.type === 'error' && (
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  {t.type === 'loading' && (
                    <svg className="w-5 h-5 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}

                  {/* Mensagem */}
                  <div className="flex-1">{t.message as any}</div>

                  {/* Botão fechar */}
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:bg-white/20 rounded-full p-1 transition-colors"
                    aria-label="Fechar notificação"
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            }}
          </Toaster>
          </ChatNotificationsProvider>
          </MenuPermissionsProvider>
        </TenantProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;


